import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { auth } from '../services/firebase';
import { apiFetch } from '../services/api';
import { Spinner, showToast } from '../components/UI';
import FeedbackPanel from './FeedbackPanel';

const PLATFORMS = ['linkedin', 'facebook', 'instagram'];

const PLATFORM_META = {
  linkedin:  { label: 'LinkedIn',  icon: '💼', color: '#0077b5' },
  facebook:  { label: 'Facebook',  icon: '📘', color: '#1877f2' },
  instagram: { label: 'Instagram', icon: '📸', color: '#e1306c' },
};

const PLATFORM_SIZES = {
  linkedin: [
    { label: 'Feed (1200x627)',        w: 1200, h: 627  },
    { label: 'Square (1200x1200)',     w: 1200, h: 1200 },
    { label: 'Square (600x600)',       w: 600,  h: 600  },
    { label: 'Banner (1584x396)',      w: 1584, h: 396  },
    { label: 'Vertical (627x1200)',    w: 627,  h: 1200 },
    { label: 'Widescreen (1920x1080)', w: 1920, h: 1080 },
  ],
  facebook: [
    { label: 'Feed (1200x630)',        w: 1200, h: 630  },
    { label: 'Square (1080x1080)',     w: 1080, h: 1080 },
    { label: 'Square (600x600)',       w: 600,  h: 600  },
    { label: 'Story (1080x1920)',      w: 1080, h: 1920 },
    { label: 'Cover (820x312)',        w: 820,  h: 312  },
    { label: 'Widescreen (1920x1080)', w: 1920, h: 1080 },
  ],
  instagram: [
    { label: 'Square (1080x1080)',     w: 1080, h: 1080 },
    { label: 'Square (600x600)',       w: 600,  h: 600  },
    { label: 'Story (1080x1920)',      w: 1080, h: 1920 },
    { label: 'Portrait (1080x1350)',   w: 1080, h: 1350 },
    { label: 'Landscape (1080x566)',   w: 1080, h: 566  },
  ],
};

const ZOOM_LEVELS = [25, 50, 75, 100];

const STATUS_META = {
  html_ready:     { label: 'HTML Ready',     color: '#8b5cf6', bg: 'rgba(139,92,246,0.1)' },
  pending_review: { label: 'Pending Review', color: '#e8a87c', bg: 'rgba(232,168,124,0.1)' },
  certified:      { label: 'Certified',      color: '#5dcaa5', bg: 'rgba(93,202,165,0.1)'  },
  published:      { label: 'Published',      color: '#378add', bg: 'rgba(55,138,221,0.1)'  },
  archived:       { label: 'Archived',       color: '#999',    bg: 'rgba(0,0,0,0.05)'      },
};

const HELP_STORAGE_KEY = 'help-campaign-page';
const HELP_TITLE       = 'Advertisement Campaign';
const HELP_MESSAGE     = 'AI CEO has generated this advertisement campaign for you. Review the ad copy for each platform, use Preview to see live changes, export as PNG when ready, and copy the post text to publish directly to your channels.';
const HELP_ACCENT      = '#378add';

// ─── useHelpBanner ────────────────────────────────────────────────────────────
function useHelpBanner(storageKey) {
  var stored = false;
  try { stored = localStorage.getItem(storageKey) === 'dismissed'; } catch(e) {}
  var _state = useState(!stored);
  var visible = _state[0];
  var setVisible = _state[1];
  var _exp = useState(false);
  var expanded = _exp[0];
  var setExpanded = _exp[1];

  var dismiss = useCallback(function() {
    setVisible(false);
    setExpanded(false);
    try { localStorage.setItem(storageKey, 'dismissed'); } catch(e) {}
  }, [storageKey]);

  var reopen = useCallback(function() {
    setVisible(true);
    try { localStorage.removeItem(storageKey); } catch(e) {}
  }, [storageKey]);

  return { visible: visible, expanded: expanded, setExpanded: setExpanded, dismiss: dismiss, reopen: reopen };
}

// ─── Shared helpers ───────────────────────────────────────────────────────────
function el(type, props) {
  var args = [type, props].concat(Array.prototype.slice.call(arguments, 2));
  return React.createElement.apply(React, args);
}

var inputStyle = {
  width: '100%', padding: '8px 10px', borderRadius: '8px',
  border: '1px solid var(--paper-3)', fontSize: '13px',
  background: 'var(--white)', color: 'var(--ink)',
  boxSizing: 'border-box', fontFamily: 'var(--sans)',
};

var sectionLabel = {
  fontSize: '10px', fontWeight: '600', letterSpacing: '0.1em',
  textTransform: 'uppercase', color: 'var(--ink-mute)', marginBottom: '8px',
};

function StatusBadge(props) {
  var m = STATUS_META[props.status] || STATUS_META.html_ready;
  return el('span', {
    style: {
      fontSize: '11px', fontWeight: '600', padding: '3px 10px',
      borderRadius: '100px', background: m.bg, color: m.color,
      textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap',
    }
  }, m.label);
}

function ActionBtn(props) {
  var variant = props.variant || 'primary';
  var variants = {
    primary: { background: 'var(--ink)',   color: 'var(--paper)', border: 'none' },
    ghost:   { background: 'var(--white)', color: 'var(--ink)',   border: '1px solid var(--paper-3)' },
  };
  return el('button', {
    onClick: props.onClick,
    disabled: props.disabled || props.loading,
    style: Object.assign({
      display: 'inline-flex', alignItems: 'center', gap: '6px',
      padding: '8px 14px', borderRadius: '8px', fontSize: '13px',
      fontWeight: '500',
      cursor: (props.disabled || props.loading) ? 'not-allowed' : 'pointer',
      opacity: (props.disabled || props.loading) ? 0.5 : 1,
      transition: 'opacity 0.15s',
    }, variants[variant], props.style || {}),
  }, props.loading ? el(Spinner, { size: 13 }) : props.children);
}

function useIsMobile() {
  var _s = useState(function() { return window.innerWidth < 900; });
  var v = _s[0]; var setV = _s[1];
  useEffect(function() {
    function h() { setV(window.innerWidth < 900); }
    window.addEventListener('resize', h);
    return function() { window.removeEventListener('resize', h); };
  }, []);
  return v;
}

// ─── HelpBanner render ────────────────────────────────────────────────────────
function renderHelpBanner(help) {
  if (!help.visible) return null;
  var isLong = HELP_MESSAGE.length > 120;
  return el('div', {
    style: {
      display: 'flex', alignItems: 'flex-start', gap: '10px',
      padding: '10px 24px', borderBottom: '1px solid rgba(55,138,221,0.18)',
      background: 'rgba(55,138,221,0.06)',
    }
  },
    el('div', { style: { width: '3px', borderRadius: '2px', background: HELP_ACCENT, alignSelf: 'stretch', flexShrink: 0, marginTop: '1px' } }),
    el('span', { style: { fontSize: '14px', marginTop: '1px', flexShrink: 0 } }, '💡'),
    el('div', { style: { flex: 1, minWidth: 0 } },
      el('span', { style: { fontSize: '12px', fontWeight: '600', color: HELP_ACCENT, marginRight: '6px' } }, HELP_TITLE + '.'),
      el('span', {
        style: {
          fontSize: '12px', color: 'var(--ink-soft)', lineHeight: 1.5,
          display: (help.expanded || !isLong) ? 'inline' : '-webkit-box',
          WebkitLineClamp: (help.expanded || !isLong) ? undefined : 1,
          WebkitBoxOrient: 'vertical',
          overflow: (help.expanded || !isLong) ? 'visible' : 'hidden',
        }
      }, HELP_MESSAGE),
      isLong ? el('button', {
        onClick: function() { help.setExpanded(function(e) { return !e; }); },
        style: {
          background: 'none', border: 'none', cursor: 'pointer', padding: '0 4px',
          fontSize: '11px', color: HELP_ACCENT, fontWeight: '500', fontFamily: 'var(--sans)',
        }
      }, help.expanded ? 'Show less ▲' : 'Show more ▼') : null
    ),
    el('button', {
      onClick: help.dismiss,
      title: 'Dismiss — use ? in header to reopen',
      style: {
        background: 'none', border: 'none', cursor: 'pointer',
        fontSize: '14px', color: 'var(--ink-mute)', padding: '0 2px',
        lineHeight: 1, flexShrink: 0, marginTop: '1px',
      }
    }, '✕')
  );
}

// ─── PlatformTab ──────────────────────────────────────────────────────────────
function PlatformTab(props) {
  var platform     = props.platform;
  var platformData = props.platformData;
  var onSave       = props.onSave;
  var onExport     = props.onExport;
  var onDownload   = props.onDownload;
  var saving       = props.saving;
  var exporting    = props.exporting;
  var downloading  = props.downloading;

  var status = (platformData && platformData._status) || 'html_ready';

  var _size = useState(PLATFORM_SIZES[platform][0]);
  var selectedSize = _size[0]; var setSelectedSize = _size[1];

  var _zoom = useState(50);
  var zoom = _zoom[0]; var setZoom = _zoom[1];

  var _copy = useState({
    headline:  (platformData && platformData.headline)  || '',
    body_copy: (platformData && platformData.body_copy) || '',
    cta:       (platformData && platformData.cta)       || '',
    post_text: (platformData && platformData.post_text) || '',
  });
  var copy = _copy[0]; var setCopy = _copy[1];

  var _ph = useState((platformData && platformData.html) || '');
  var previewHtml = _ph[0]; var setPreviewHtml = _ph[1];

  var _dirty = useState(false);
  var dirty = _dirty[0]; var setDirty = _dirty[1];

  var _png = useState(false);
  var showPng = _png[0]; var setShowPng = _png[1];

  var _key = useState(0);
  var iframeKey = _key[0]; var setIframeKey = _key[1];

  useEffect(function() {
    if (!platformData) return;
    setCopy({
      headline:  platformData.headline  || '',
      body_copy: platformData.body_copy || '',
      cta:       platformData.cta       || '',
      post_text: platformData.post_text || '',
    });
    setPreviewHtml(platformData.html || '');
    setIframeKey(function(k) { return k + 1; });
    setDirty(false);
  }, [platformData]);

  function handleCopyChange(field, value) {
    setCopy(function(prev) {
      var next = Object.assign({}, prev);
      next[field] = value;
      return next;
    });
    setDirty(true);
  }

  function buildUpdatedHtml(baseHtml, currentCopy) {
    var parser = new DOMParser();
    var doc = parser.parseFromString(baseHtml, 'text/html');
    var idMap = { headline: 'raji-headline', body_copy: 'raji-body', cta: 'raji-cta' };
    Object.keys(idMap).forEach(function(field) {
      var domEl = doc.getElementById(idMap[field]);
      if (domEl) domEl.innerText = currentCopy[field] || '';
    });
    return doc.documentElement.outerHTML;
  }

  function handlePreview() {
    var updated = buildUpdatedHtml((platformData && platformData.html) || previewHtml, copy);
    setPreviewHtml(updated);
    setIframeKey(function(k) { return k + 1; });
  }

  function handleSave() {
    var updated = buildUpdatedHtml((platformData && platformData.html) || previewHtml, copy);
    setPreviewHtml(updated);
    setIframeKey(function(k) { return k + 1; });
    onSave(platform, Object.assign({}, copy, { html: updated }));
    setDirty(false);
  }

  var pngUrl = (platformData && (platformData._perm_gcs_signed_url || platformData._tmp_gcs_signed_url));
  var hasPng = !!pngUrl;
  var scale  = zoom / 100;
  var scaledW = Math.round(selectedSize.w * scale);
  var scaledH = Math.round(selectedSize.h * scale);

  return el('div', { style: { display: 'flex', flexDirection: 'column', gap: '20px' } },

    // Status row
    el('div', { style: { display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' } },
      el(StatusBadge, { status: status }),
      dirty ? el('span', { style: { fontSize: '11px', color: '#e8a87c', fontWeight: '500' } }, '● Unsaved changes') : null
    ),

    // ── Controls bar ──
    el('div', {
      style: {
        display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap',
        padding: '10px 12px', borderRadius: '10px',
        background: 'var(--white)', border: '1px solid var(--paper-3)',
      }
    },
      // Size dropdown
      el('select', {
        value: selectedSize.w + 'x' + selectedSize.h,
        onChange: function(e) {
          var parts = e.target.value.split('x').map(Number);
          var opt = PLATFORM_SIZES[platform].find(function(o) { return o.w === parts[0] && o.h === parts[1]; });
          if (opt) setSelectedSize(opt);
        },
        style: {
          padding: '5px 10px', borderRadius: '8px', fontSize: '12px',
          border: '1px solid var(--paper-3)', background: 'var(--paper)',
          color: 'var(--ink)', cursor: 'pointer', fontFamily: 'var(--sans)',
        }
      },
        PLATFORM_SIZES[platform].map(function(o) {
          return el('option', { key: o.w + 'x' + o.h, value: o.w + 'x' + o.h }, o.label);
        })
      ),

      // Zoom buttons
      el('div', { style: { display: 'flex', gap: '3px', marginLeft: '4px' } },
        ZOOM_LEVELS.map(function(z) {
          return el('button', {
            key: z,
            onClick: function() { setZoom(z); },
            style: {
              padding: '4px 9px', borderRadius: '6px', fontSize: '11px', fontWeight: '500',
              cursor: 'pointer', border: '1px solid var(--paper-3)',
              background: zoom === z ? 'var(--ink)' : 'transparent',
              color: zoom === z ? 'var(--paper)' : 'var(--ink-soft)',
              transition: 'all 0.12s',
            }
          }, z + '%');
        })
      ),

      el('div', { style: { flex: 1 } }),

      // Preview button
      dirty ? el(ActionBtn, {
        onClick: handlePreview, variant: 'ghost',
        style: { fontSize: '12px', padding: '5px 10px' }
      }, '👁 Preview') : null,

      // Save button
      el(ActionBtn, {
        onClick: handleSave, loading: saving, disabled: !dirty,
        variant: dirty ? 'primary' : 'ghost',
        style: { fontSize: '12px', padding: '5px 10px' }
      }, '💾 Save'),

      // Export button
      el(ActionBtn, {
        onClick: function() { onExport(platform, selectedSize); },
        loading: exporting, variant: 'ghost', disabled: dirty,
        style: { fontSize: '12px', padding: '5px 10px' }
      }, hasPng ? '🖼 Re-export' : '🖼 Export PNG')
    ),

    // ── iframe preview ──
    el('div', null,
      el('div', { style: { fontSize: '11px', color: 'var(--ink-mute)', marginBottom: '6px' } },
        selectedSize.w + ' × ' + selectedSize.h + 'px — ' + zoom + '% zoom'
      ),
      el('div', {
        style: {
          width: scaledW + 'px', height: scaledH + 'px',
          overflow: 'hidden', borderRadius: '10px',
          border: '1px solid var(--paper-3)', background: '#fff',
          position: 'relative',
        }
      },
        previewHtml
          ? el('iframe', {
              key: iframeKey,
              srcDoc: previewHtml,
              title: platform + ' ad',
              sandbox: 'allow-scripts',
              style: {
                width: selectedSize.w + 'px', height: selectedSize.h + 'px',
                border: 'none', display: 'block',
                transformOrigin: 'top left',
                transform: 'scale(' + scale + ')',
              }
            })
          : el('div', { style: { padding: '40px', textAlign: 'center', color: 'var(--ink-mute)', fontSize: '13px' } },
              'No HTML preview available'
            )
      )
    ),

    // PNG toggle
    hasPng ? el('div', null,
      el('button', {
        onClick: function() { setShowPng(function(p) { return !p; }); },
        style: {
          background: 'none', border: 'none', cursor: 'pointer',
          fontSize: '12px', color: 'var(--blue)', fontWeight: '500', padding: 0,
        }
      }, showPng ? '▲ Hide exported PNG' : '▼ Show exported PNG'),
      showPng ? el('div', {
        style: { marginTop: '10px', border: '1px solid var(--paper-3)', borderRadius: '10px', overflow: 'hidden' }
      }, el('img', { src: pngUrl, alt: platform + ' PNG', style: { width: '100%', display: 'block' } })) : null
    ) : null,

    // ── Copy fields ──
    el('div', {
      style: {
        background: 'var(--white)', border: '1px solid var(--paper-3)',
        borderRadius: '12px', padding: '20px',
        display: 'flex', flexDirection: 'column', gap: '16px',
      }
    },
      el('div', { style: sectionLabel }, 'Ad Copy'),

      ['headline', 'body_copy', 'cta'].map(function(key) {
        var labelMap = { headline: 'Headline', body_copy: 'Body copy', cta: 'CTA' };
        return el('div', { key: key },
          el('label', {
            style: {
              fontSize: '11px', fontWeight: '600', textTransform: 'uppercase',
              letterSpacing: '0.06em', color: 'var(--ink-mute)', display: 'block', marginBottom: '5px',
            }
          }, labelMap[key]),
          el('input', {
            value: copy[key],
            onChange: function(e) { handleCopyChange(key, e.target.value); },
            style: inputStyle,
          })
        );
      }),

      el('div', null,
        el('label', {
          style: {
            fontSize: '11px', fontWeight: '600', textTransform: 'uppercase',
            letterSpacing: '0.06em', color: 'var(--ink-mute)', display: 'block', marginBottom: '5px',
          }
        }, 'Post text'),
        el('textarea', {
          value: copy.post_text,
          onChange: function(e) { handleCopyChange('post_text', e.target.value); },
          rows: 5,
          style: Object.assign({}, inputStyle, { resize: 'vertical', lineHeight: 1.6 }),
        })
      ),

      el('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: '8px' } },
        dirty ? el(ActionBtn, { onClick: handlePreview, variant: 'ghost' }, '👁 Preview draft changes') : null,
        el(ActionBtn, {
          onClick: handleSave, loading: saving, disabled: !dirty,
          variant: dirty ? 'primary' : 'ghost',
        }, '💾 Save changes')
      )
    ),

    // ── Bottom actions ──
    el('div', { style: { display: 'flex', flexWrap: 'wrap', gap: '10px' } },
      el(ActionBtn, {
        onClick: function() { onExport(platform, selectedSize); },
        loading: exporting, variant: 'ghost', disabled: dirty,
      }, hasPng ? '🖼 Re-export PNG' : '🖼 Export PNG'),

      hasPng ? el(ActionBtn, {
        onClick: function() { onDownload(platform); },
        loading: downloading, variant: 'ghost',
      }, '⬇ Download PNG') : null,

      copy.post_text ? el(ActionBtn, {
        onClick: function() {
          var p = (navigator.clipboard && navigator.clipboard.writeText(copy.post_text));
          if (p) {
            p.then(function() { showToast('Post text copied!'); })
             .catch(function() { fallbackCopy(copy.post_text); showToast('Post text copied!'); });
          } else {
            fallbackCopy(copy.post_text);
            showToast('Post text copied!');
          }
        },
        variant: 'ghost',
      }, '📋 Copy post text') : null,

      dirty ? el('span', { style: { fontSize: '12px', color: '#e8a87c', alignSelf: 'center' } }, 'Save before exporting') : null
    )
  );
}

function fallbackCopy(text) {
  var ta = document.createElement('textarea');
  ta.value = text;
  document.body.appendChild(ta);
  ta.select();
  document.execCommand('copy');
  document.body.removeChild(ta);
}

// ─── BottomBar ────────────────────────────────────────────────────────────────
function BottomBar(props) {
  var body = props.body; var platforms = props.platforms;
  var activeTab = props.activeTab; var setActiveTab = props.setActiveTab;
  var schedule = props.schedule;

  return el('div', {
    style: {
      borderTop: '1px solid var(--paper-3)', background: 'var(--white)',
      padding: '16px 24px',
      display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '20px',
    }
  },
    el('div', null,
      el('div', { style: sectionLabel }, 'Platform Status'),
      el('div', { style: { display: 'flex', flexDirection: 'column', gap: '6px' } },
        platforms.map(function(p) {
          var m = PLATFORM_META[p];
          var status = (body && body.platforms && body.platforms[p] && body.platforms[p]._status) || 'html_ready';
          return el('button', {
            key: p,
            onClick: function() { setActiveTab(p); },
            style: {
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '7px 10px', borderRadius: '8px', cursor: 'pointer',
              border: '1px solid ' + (activeTab === p ? m.color : 'var(--paper-3)'),
              background: activeTab === p ? m.color + '08' : 'transparent', width: '100%',
            }
          },
            el('span', { style: { fontSize: '13px', fontWeight: '500', color: 'var(--ink)', display: 'flex', alignItems: 'center', gap: '6px' } },
              m.icon + ' ' + m.label
            ),
            el(StatusBadge, { status: status })
          );
        })
      )
    ),

    schedule && Object.keys(schedule).length > 0 ? el('div', null,
      el('div', { style: sectionLabel }, 'Suggested Schedule'),
      el('div', { style: { display: 'flex', flexDirection: 'column', gap: '6px' } },
        platforms.map(function(p) {
          if (!schedule[p]) return null;
          return el('div', { key: p, style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' } },
            el('span', { style: { fontSize: '12px', color: 'var(--ink-mute)' } }, PLATFORM_META[p].icon + ' ' + PLATFORM_META[p].label),
            el('span', { style: { fontSize: '12px', fontWeight: '500' } },
              new Date(schedule[p]).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
            )
          );
        })
      )
    ) : null,

    body && body.strategy ? el('div', null,
      el('div', { style: sectionLabel }, 'Strategy'),
      el('div', { style: { display: 'flex', flexDirection: 'column', gap: '6px' } },
        [['Objective', body.strategy.objective], ['Audience', body.strategy.target_audience], ['Type', body.strategy.campaign_type]]
          .map(function(pair) {
            if (!pair[1]) return null;
            return el('div', { key: pair[0] },
              el('div', { style: { fontSize: '10px', color: 'var(--ink-mute)', textTransform: 'uppercase', letterSpacing: '0.06em' } }, pair[0]),
              el('div', { style: { fontSize: '12px', color: 'var(--ink)', lineHeight: 1.4 } }, pair[1])
            );
          })
      )
    ) : null
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function CampaignPage() {
  var _params = useParams(); var id = _params.id;
  var navigate    = useNavigate();
  var isMobile    = useIsMobile();
  var currentUserId = auth.currentUser ? auth.currentUser.uid : null;

  var _camp = useState(null);      var campaign    = _camp[0];    var setCampaign    = _camp[1];
  var _load = useState(true);      var loading     = _load[0];    var setLoading     = _load[1];
  var _err  = useState(null);      var error       = _err[0];     var setError       = _err[1];
  var _tab  = useState('linkedin');var activeTab   = _tab[0];     var setActiveTab   = _tab[1];
  var _sav  = useState(false);     var saving      = _sav[0];     var setSaving      = _sav[1];
  var _exp  = useState(null);      var exporting   = _exp[0];     var setExporting   = _exp[1];
  var _dl   = useState(null);      var downloading = _dl[0];      var setDownloading = _dl[1];

  // Help banner
  var help = useHelpBanner(HELP_STORAGE_KEY);

  var fetchCampaign = useCallback(function() {
    setLoading(true); setError(null);
    return apiFetch('/api/campaigns/' + id)
      .then(function(data) { setCampaign(data); })
      .catch(function(e) { setError(e.message || 'Failed to load campaign'); })
      .finally(function() { setLoading(false); });
  }, [id]);

  useEffect(function() { fetchCampaign(); }, [fetchCampaign]);

  var handleSave = useCallback(function(platform, copy) {
    setSaving(true);
    var body = {};
    body[platform] = copy;
    return apiFetch('/api/campaigns/' + id, {
      method: 'PATCH',
      body: JSON.stringify({ platforms: body }),
    })
      .then(function() { showToast('Changes saved ✓'); return fetchCampaign(); })
      .catch(function(e) { showToast(e.message || 'Save failed'); })
      .finally(function() { setSaving(false); });
  }, [id, fetchCampaign]);

  var handleExport = useCallback(function(platform, size) {
    setExporting(platform);
    return apiFetch('/api/campaigns/' + id + '/export/' + platform, {
      method: 'POST',
      body: JSON.stringify({ width: size.w, height: size.h }),
    })
      .then(function() { showToast(PLATFORM_META[platform].label + ' PNG exported ✓'); return fetchCampaign(); })
      .catch(function(e) { showToast(e.message || 'Export failed'); })
      .finally(function() { setExporting(null); });
  }, [id, fetchCampaign]);

  var handleDownload = useCallback(function(platform) {
    setDownloading(platform);
    return apiFetch('/api/campaigns/' + id + '/download/' + platform)
      .then(function(res) {
        var a = document.createElement('a');
        a.href = res.signed_url; a.download = res.filename; a.target = '_blank';
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        showToast('Downloading ' + res.filename);
      })
      .catch(function(e) { showToast(e.message || 'Download failed'); })
      .finally(function() { setDownloading(null); });
  }, [id]);

  if (loading) return el('div', { style: centeredStyle }, el(Spinner, { size: 28 }));
  if (error || !campaign) return el('div', { style: centeredStyle },
    el('p', { style: { color: 'var(--ink-mute)', marginBottom: '16px' } }, error || 'Campaign not found'),
    el('button', { onClick: function() { navigate('/dashboard/ceo-runlog'); }, style: ghostBtn }, '← CEO Report')
  );

  var body     = campaign.campaign || {};
  var schedule = body.posting_schedule || {};

  return el('div', { style: { display: 'flex', flexDirection: 'column', height: '100vh', background: 'var(--paper)', overflow: 'hidden' } },

    // ── Header ──
    el('header', {
      style: {
        height: '52px', flexShrink: 0, zIndex: 50,
        background: 'rgba(245,242,235,0.95)', backdropFilter: 'blur(10px)',
        borderBottom: '1px solid var(--paper-3)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 24px', gap: '12px',
      }
    },
      // Left
      el('div', { style: { display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0, flex: 1 } },
        el('button', { onClick: function() { navigate('/dashboard/ceo-runlog'); }, style: ghostBtn }, '← CEO Report'),
        el('div', { style: { width: '1px', height: '20px', background: 'var(--paper-3)' } }),
        el('span', { style: { fontSize: '14px', fontWeight: '500', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } },
          '📣 ' + campaign.title
        )
      ),
      // Right — ? help button + version
      el('div', { style: { display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 } },
        // ? button appears only when banner is dismissed
        !help.visible ? el('button', {
          onClick: help.reopen,
          title: 'Show page help',
          style: {
            width: '26px', height: '26px', borderRadius: '50%',
            background: 'none', border: '1.5px solid ' + HELP_ACCENT,
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '12px', fontWeight: '700', color: HELP_ACCENT,
            transition: 'all 0.15s', flexShrink: 0,
          }
        }, '?') : null,
        el('span', { style: { fontSize: '11px', color: 'var(--ink-mute)' } }, 'v' + campaign.version)
      )
    ),

    // ── Help banner (below header, above content) ──
    renderHelpBanner(help),

    // ── Scrollable content ──
    el('div', { style: { flex: 1, overflowY: 'auto', minHeight: 0 } },
      el('div', { style: { padding: isMobile ? '16px 14px' : '28px 40px' } },

        // Platform tabs
        el('div', { style: { display: 'flex', gap: '4px', borderBottom: '1px solid var(--paper-3)', marginBottom: '24px' } },
          PLATFORMS.map(function(p) {
            var m = PLATFORM_META[p];
            var status = (body.platforms && body.platforms[p] && body.platforms[p]._status) || 'html_ready';
            var sm = STATUS_META[status];
            return el('button', {
              key: p,
              onClick: function() { setActiveTab(p); },
              style: {
                display: 'flex', alignItems: 'center', gap: '6px',
                padding: '10px 16px', border: 'none', cursor: 'pointer', background: 'none',
                fontSize: '13px', fontWeight: activeTab === p ? '600' : '400',
                color: activeTab === p ? m.color : 'var(--ink-soft)',
                borderBottom: activeTab === p ? '2px solid ' + m.color : '2px solid transparent',
                marginBottom: '-1px', transition: 'all 0.15s',
              }
            },
              el('span', null, m.icon),
              !isMobile ? el('span', null, m.label) : null,
              el('span', { style: { width: '6px', height: '6px', borderRadius: '50%', background: sm.color } })
            );
          })
        ),

        // Active platform tabs
        PLATFORMS.map(function(p) {
          return el('div', { key: p, style: { display: activeTab === p ? 'block' : 'none' } },
            el(PlatformTab, {
              platform: p,
              platformData: body.platforms && body.platforms[p],
              onSave: handleSave,
              onExport: handleExport,
              onDownload: handleDownload,
              saving: saving,
              exporting: exporting === p,
              downloading: downloading === p,
            })
          );
        }),

        // Feedback
        el('div', { style: { marginTop: '32px', paddingTop: '24px', borderTop: '1px solid var(--paper-3)' } },
          el(FeedbackPanel, {
            entityType: 'artifact',
            entityId: id,
            currentUserId: currentUserId,
            isMobile: isMobile,
          })
        ),

        // Bottom bar
        el('div', { style: { marginTop: '32px' } },
          el(BottomBar, {
            body: body,
            platforms: PLATFORMS,
            activeTab: activeTab,
            setActiveTab: setActiveTab,
            schedule: schedule,
          })
        )
      )
    )
  );
}

var centeredStyle = { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', gap: '12px' };
var ghostBtn      = { background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', color: 'var(--ink-soft)', padding: '4px 0' };